/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useHeaderStates } from '../../Providers/HeaderStates';
import { RotatingBanner } from '../../components/RotatingBanner';
import { useStartupState } from '../../Providers/StartupState';
import { getAssetUrl, getRawAssetUrl, handleGetData } from '../../Api';
import { useResilientAssetUrl } from '../../hooks/useResilientAssetUrl';
import { useReveal } from '../../hooks/useReveal';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../../components/Loading';
import { CartaoVitrine, Vitrine } from '../../components/Vitrine';
import { dataPorExtenso } from '../PhotoGallery/dados';
import * as S from './style';
import ReactPlayer from 'react-player';
import { SocialMediaButtons } from '../../components/SocialMediaButtons';
import { ArrowRight, CircleStop, Expand, Images, Play, PlayCircle, Share } from 'lucide-react';

// Distribuição dos cartões no bento de 4 colunas: o primeiro é o destaque
// (2 colunas), os dois seguintes fecham a linha, e os dois últimos ocupam
// metade cada. Cartão além do quinto cai em 1 coluna.
const ACESSO_SPANS = [2, 1, 1, 2, 2];

/* Limites da seção "Nossos Negócios", e eles vivem SÓ aqui.
   O painel tem altura fixa (25rem) e cabem ~117px de texto além do maior item
   de hoje: uma linha extra na manchete e duas na descrição. Esses números saem
   dessa geometria.
   Não vão para o Squidex como maxLength de propósito. Limite tirado da altura
   de um painel é restrição de apresentação, e o schema descreve o conteúdo --
   `negocio` pode ser usado amanhã numa tela que comporte texto longo, e ali o
   limite desta tela não faz sentido nenhum. É a mesma razão pela qual cor,
   largura e "abrir em aba nova" também ficaram no código. */
const LIMITE_NEGOCIO = { nome: 30, titulo: 90, descricao: 260 };

/* Corta no limite e marca o corte. Sem a marca, o texto termina no meio de uma
   frase e parece erro de quem escreveu; com ela, fica claro que o site cortou.
   Não parte palavra: recua até o último espaço, desde que isso não coma mais de
   40% do texto (numa palavra única gigante, corta na força). */
const cortar = (texto, limite) => {
  const valor = String(texto ?? '');
  if (valor.length <= limite) return texto;
  const bruto = valor.slice(0, limite);
  const ultimoEspaco = bruto.lastIndexOf(' ');
  const base = ultimoEspaco > limite * 0.6 ? bruto.slice(0, ultimoEspaco) : bruto;
  return `${base.replace(/[\s,;:.]+$/, '')} [...]`;
};

// Cor da faixa superior de cada tile. Fica no código, e não no campo `cor` do
// cartão, porque "não repetir" é propriedade do conjunto: um campo por item não
// tem como saber o que os vizinhos escolheram -- era daí que vinham dois verdes
// escuros iguais lado a lado.
//
// A ordem acompanha o bento: o lime do segundo é o mesmo tom da figura na
// ilustração do "Trabalhe Conosco", e o âmbar fecha no cartão de denúncias, que
// já é o de alerta no resto do site. Os três verdes ficam separados por valor
// (médio, folha, profundo) para não encostarem na vertical.
const ACESSO_CORES = [
  { faixa: 'var(--second-color)' }, // #00893a  verde institucional
  // O lime é o tom da figura na ilustração, mas como texto sobre branco não
  // chega nem perto de 4.5:1 nem depois do escurecimento -- então a faixa fica
  // no lime e o texto usa o mesmo tom rebaixado.
  { faixa: 'var(--third-color)', texto: '#6f7d00' }, // #c7d300  lime
  { faixa: 'var(--fourth-color)' }, // #66b336  verde folha
  { faixa: 'var(--first-color)' }, // #0f4e2b  verde profundo
  { faixa: 'var(--sixth-color)' }, // #fbb81c  âmbar
];

// Cada slide precisa do próprio hook de imagem resiliente (regras de hooks
// não permitem chamá-lo direto dentro do .map do carrossel).
const HeroSlide = ({ item, index, onAbrir }) => {
  // avoidWebpForGif: alguns banners do carrossel usam GIF animado, e pedir a
  // conversão pra webp no resize do Squidex achata o GIF no primeiro frame.
  // O hook checa o Content-Type real do arquivo e só aplica webp quando não
  // for gif, então banners estáticos continuam ganhando a otimização.
  const bgUrl = useResilientAssetUrl(item?.imagemFundo?.[0], {
    width: 1600,
    quality: 82,
    format: 'webp',
    avoidWebpForGif: true,
  });

  // Slide sem destino existe -- o último do carrossel não tem. Antes o botão
  // aparecia igual e abria a string "undefined" numa aba nova.
  const temDestino = Boolean(item?.acao?.destino || item?.acao?.arquivo?.[0]);

  // Imagem de destaque é a exceção: quase todo slide traz só a arte de fundo,
  // com a peça já montada dentro dela. Sem esta guarda o <img> era renderizado
  // mesmo sem arquivo, e o navegador desenhava o texto do alt por cima do
  // banner -- o "imagem do banner" que aparecia na tela.
  const destaque = item?.imagemDestaque?.[0];

  return (
    <S.BannerBox img={bgUrl} tabIndex="0">
      <S.ContentBanner>
        <h2 tabIndex="0">{item?.titulo}</h2>
        <span tabIndex="0">{item?.subtitulo}</span>
        {temDestino && (
          <S.ReadMoreButton tabIndex="0" onClick={() => onAbrir(item?.acao)}>
            {item?.acao?.rotulo}
          </S.ReadMoreButton>
        )}
      </S.ContentBanner>
      {destaque && (
        <img
          tabIndex="0"
          src={getAssetUrl(destaque, {
            width: 480,
            quality: 85,
            format: 'webp'
          })}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = getRawAssetUrl(destaque);
          }}
          alt={`imagem do banner ${item?.titulo || ''}`}
          loading={index === 0 ? 'eager' : 'lazy'}
          decoding="async"
        />
      )}
    </S.BannerBox>
  );
};

export const Home = () => {
  const [data, setData] = useState(null);
  const [dataReady, setDataReady] = useState(false);
  const [clickVideoOpen, setClickVideoOpen] = useState(false);
  const [noticias, setNoticias] = useState(null);
  const [noticiasReady, setNoticiasReady] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [videoPlay, setVideoPlay] = useState(false);
  const [srcModal] = useState(false);
  const [openSocialMedia, setOpenSocialMedia] = useState(false);

  const { setTitle, setBgHeader } = useHeaderStates();
  const { token, setError, setLoading } = useStartupState();
  const navigate = useNavigate();

  // Acesso rápido: cabeçalho e bento aparecem ao entrar na viewport.
  const [headRef, headVisible] = useReveal();
  const [bentoRef, bentoVisible] = useReveal();
  const [bizRef, bizVisible] = useReveal();

  // Nossos negócios: qual frente de trabalho está aberta no painel.
  const [negocioAtivo, setNegocioAtivo] = useState(0);
  const abasRef = useRef(null);

  // O componente `acao` do Squidex traz rótulo, destino e arquivo. Não existe
  // campo dizendo se o destino é interno ou externo, de propósito: rota do site
  // começa com "/", e é isso que decide. Campo separado só criaria a chance de
  // alguém marcar "interno" num endereço externo.
  const abrirAcao = (acao) => {
    const arquivo = acao?.arquivo?.[0];
    if (arquivo) {
      window.open(getRawAssetUrl(arquivo), '_blank');
      return;
    }

    const destino = acao?.destino;
    if (!destino) return;

    if (destino.startsWith('/')) {
      navigate(destino);
    } else {
      window.open(destino, '_blank');
    }
  };


  useEffect(() => {
    setError(null);
    setLoading(true);
    if (token) {
      handleGetData(token, 'api/content/copasul/pagina-inicio/')
        .then((response) => {
          setData(response?.data?.items[0]?.data);
          if (response?.status !== 200) {
            throw new Error(response.message);
          }
        })
        .catch((e) => {
          setData(null);
          console.log(e.message);
        })
        .finally(() => {
          setDataReady(true);
        });
    }
  }, [token]);

  // Quantas notícias o rail carrega vem do Squidex. Campo vazio não vira número
  // escrito aqui: o $top simplesmente não é enviado, e quem responde é o padrão
  // do próprio Squidex. Antes esse número era fixo em 16 no código enquanto o
  // Squidex dizia 8, e ninguém percebia que o campo não valia nada.
  const quantidadeDeNoticias = data?.ultimasNoticias?.iv?.quantidade;

  // Esta busca espera a da página, porque o $top depende de um campo dela. Custa
  // uma ida a mais ao servidor; em troca, o número no Squidex é o número que o
  // site usa. Buscar um teto fixo e cortar no cliente seria mais rápido e
  // mentiria quando alguém pedisse mais que o teto.
  useEffect(() => {
    if (!token || !dataReady) return;
    setError(null);
    setLoading(true);
    handleGetData(
      token,
      // Ordem explícita, como a página de notícias já faz: sem $orderby o
      // Squidex devolve na ordem dele, e o rail acabava fora de ordem --
      // aparece na hora que os itens têm lastModified parecido.
      // Só os campos do cartão: o texto da matéria fica de fora.
      `api/content/copasul/materia/?$orderby=data/data/iv desc${
        quantidadeDeNoticias ? `&$top=${quantidadeDeNoticias}` : ''
      }`,
      { 'X-Fields': 'titulo,data,resumo,capa,endereco' },
    )
      .then((response) => {
        setNoticias(response?.data?.items);
        if (response?.status !== 200) {
          throw new Error(response.message);
        }
      })
      .catch((e) => {
        setNoticias([]);
        console.log(e.message);
      })
      .finally(() => {
        setNoticiasReady(true);
      });
  }, [token, dataReady, quantidadeDeNoticias]);

  useEffect(() => {
    if (dataReady && noticiasReady) {
      setLoading(false);
    }
  }, [dataReady, noticiasReady]);

  useEffect(() => {
    setTitle(null);
    setBgHeader(null);
  });

  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [openModal]);

  const negocios = data?.negocios?.iv;
  // A lista pode encurtar entre um deploy e outro; sem o clamp, um índice
  // guardado apontaria para fora e o painel ficaria em branco.
  const frentes = negocios?.itens || [];
  const frenteAberta = frentes[Math.min(negocioAtivo, frentes.length - 1)];

  // Foto de cada frente, no mesmo tamanho em que o painel mostra.
  const fotoDaFrente = (id) => getAssetUrl(id, { width: 900, quality: 85, format: 'webp' });

  // Baixa as fotos de todas as frentes logo que a Home tem os dados: ao trocar
  // de aba a foto já está no cache. Sem isto, a primeira vez de cada uma levava
  // ~2 s (o Squidex gera o tamanho na hora) com a foto da aba anterior na tela.
  const fotosDasFrentes = frentes.map((f) => f?.imagem?.[0]).filter(Boolean).join(',');
  useEffect(() => {
    if (!fotosDasFrentes) return;
    fotosDasFrentes.split(',').forEach((id) => {
      const img = new Image();
      img.src = fotoDaFrente(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fotosDasFrentes]);

  // Abas de verdade respondem às setas do teclado, não só ao clique.
  const irParaFrente = (destino) => {
    if (!frentes.length) return;
    const i = (destino + frentes.length) % frentes.length;
    setNegocioAtivo(i);
    // O foco acompanha, senão o leitor de tela continua anunciando a aba antiga.
    abasRef.current?.querySelectorAll('[role="tab"]')[i]?.focus();
  };

  const teclasDasAbas = (e) => {
    const passos = {
      ArrowDown: negocioAtivo + 1,
      ArrowRight: negocioAtivo + 1,
      ArrowUp: negocioAtivo - 1,
      ArrowLeft: negocioAtivo - 1,
      Home: 0,
      End: frentes.length - 1,
    };
    if (!(e.key in passos)) return;
    e.preventDefault();
    irParaFrente(passos[e.key]);
  };

  const faixaVideo = data?.faixaVideo?.iv;
  const faixaPodcast = data?.faixaPodcast?.iv;
  const acessoRapido = data?.acessoRapido?.iv;
  const ultimasNoticias = data?.ultimasNoticias?.iv;

  const videoBannerBgUrl = useResilientAssetUrl(faixaVideo?.imagemFundo?.[0], {
    width: 1920, quality: 82, format: 'webp',
  });
  const videoPhoneMoldUrl = useResilientAssetUrl(faixaVideo?.imagemMoldura?.[0], {
    width: 750, quality: 85, format: 'webp',
  });

  if (!dataReady || !noticiasReady) {
    return <Loading />;
  } else {
    return (
      <S.Container>
        {openModal && (
          <S.ModalOverlay>
            <S.ModalContent onClick={(e) => e.stopPropagation()}>
              <div>
                {openSocialMedia && (
                  <SocialMediaButtons
                    style={{
                      left: '5%',
                      marginTop: '2rem',
                    }}
                    open={() => {
                      setOpenSocialMedia(!openSocialMedia);
                    }}
                    url={`https://www.youtube.com/watch?v=${srcModal.data}`}
                  />
                )}
              </div>
              <S.ButtonsController>
                <S.CloseButton title='Compartilhar' onClick={() => setOpenSocialMedia(!openSocialMedia)}><Share class="icon" /></S.CloseButton>
                <S.CloseButton title='Galeria de Vídeos' onClick={() => navigate('/galeriaDeVideos')}><Images class="icon" /></S.CloseButton>
                <S.CloseButton title='Fechar' onClick={() => setOpenModal(false)}>×</S.CloseButton>
              </S.ButtonsController>

              <ReactPlayer
                className="react-player"
                url={faixaVideo?.enderecoDoVideo}
                width="100%"
                height="100%"
                playing
                controls
              />
            </S.ModalContent>
          </S.ModalOverlay>
        )}

        <div className="banner">
          <RotatingBanner
            slidesToShow={1}
            slidesToScroll={1}
            autoplay={true}
            /* 6s dava ~5s parado: a transição sozinha come 1s, e nesse tempo
               não dá para ler o card, que tem título, parágrafo e botão. */
            autoplaySpeed={9000}
            pauseOnHover={false}
            pauseOnFocus={false}
            pauseOnDotsHover={false}
            waitForAnimate={false}
          >
            {data?.carrossel?.iv?.itens?.map((item, index) => (
              // Item de lista de componente não tem id próprio; a posição é a
              // única chave estável que existe aqui.
              <HeroSlide
                key={`slide-${index}`}
                item={item}
                index={index}
                onAbrir={abrirAcao}
              />
            ))}
          </RotatingBanner>
        </div>
        <S.Faixa aria-labelledby="noticias-t">
          <S.Interior>
            <Vitrine
              id="noticias-t"
              chapeu={ultimasNoticias?.chapeu}
              titulo={ultimasNoticias?.titulo}
              rotuloAnterior="Notícia anterior"
              rotuloProximo="Próxima notícia"
            >
              {noticias &&
                noticias.map((item) => (
                  <CartaoVitrine
                    key={item?.id}
                    imagem={item?.data?.capa?.iv?.[0]}
                    etiqueta={`${dataPorExtenso(item?.data?.data?.iv)} · Notícias`}
                    titulo={item?.data?.titulo?.iv}
                    texto={item?.data?.resumo?.iv}
                    acao="Leia mais"
                    para={`/noticias/${item?.data?.endereco?.iv}`}
                  />
                ))}
            </Vitrine>
          </S.Interior>
        </S.Faixa>

        <S.Faixa $verde aria-labelledby="acessos-t">
          <S.Interior>
            <S.Reveal ref={headRef} $visible={headVisible}>
              <S.AccessHead>
                <S.AccessEyebrow tabIndex="0">
                  {acessoRapido?.chapeu}
                </S.AccessEyebrow>
                <h2 id="acessos-t" tabIndex="0">
                  {acessoRapido?.titulo}
                </h2>
                <S.AccessRule />
              </S.AccessHead>
            </S.Reveal>

            <S.Reveal ref={bentoRef} $visible={bentoVisible}>
              <S.Bento>
                {acessoRapido?.cartoes?.map((cartao, index) => {
                  // Cor é decisão de apresentação e vive aqui: além do quinto
                  // tile o padrão volta ao verde institucional.
                  const paleta = ACESSO_CORES[index] || ACESSO_CORES[0];
                  const cor = paleta.faixa;
                  const corTexto = paleta.texto || paleta.faixa;

                  return (
                    <S.AccessTile
                      key={`${cartao?.titulo}-${index}`}
                      $span={ACESSO_SPANS[index] ?? 1}
                      $color={cor}
                    >
                      {cartao?.imagem?.[0] && (
                        <S.AccessMedia
                          src={getAssetUrl(cartao.imagem[0], {
                            width: 900,
                            quality: 85,
                            format: 'webp',
                          })}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = getRawAssetUrl(cartao.imagem[0]);
                          }}
                          alt={`imagem do cartão ${cartao?.titulo}`}
                          tabIndex="0"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <S.AccessBody>
                        <h3 tabIndex="0">{cartao?.titulo}</h3>
                        <p tabIndex="0">{cartao?.descricao}</p>
                        {(cartao?.acao?.destino ||
                          cartao?.acao?.arquivo?.[0]) && (
                          <S.AccessLink
                            $color={corTexto}
                            type="button"
                            onClick={() => abrirAcao(cartao?.acao)}
                            tabIndex="0"
                          >
                            <span>
                              {cartao?.acao?.rotulo}
                            </span>
                            <ArrowRight size={16} className="arrow-icon" />
                          </S.AccessLink>
                        )}
                      </S.AccessBody>
                    </S.AccessTile>
                  );
                })}
              </S.Bento>
            </S.Reveal>
          </S.Interior>
        </S.Faixa>

        {Boolean(frentes.length) && (
          <S.Faixa aria-labelledby="negocios-t">
            <S.Interior>
              <S.Reveal ref={bizRef} $visible={bizVisible}>
                <S.BizHead>
                  <div>
                    <S.BizEyebrow tabIndex="0">{negocios?.chapeu}</S.BizEyebrow>
                    <h2 id="negocios-t" tabIndex="0">
                      {negocios?.titulo}
                    </h2>
                  </div>
                  {(negocios?.acao?.destino || negocios?.acao?.arquivo?.[0]) && (
                    <S.BizAllLink
                      type="button"
                      onClick={() => abrirAcao(negocios?.acao)}
                    >
                      {negocios?.acao?.rotulo}
                      <ArrowRight size={16} className="arrow-icon" />
                    </S.BizAllLink>
                  )}
                </S.BizHead>

                <S.BizPanel>
                  <S.BizTabs
                    ref={abasRef}
                    role="tablist"
                    aria-label="Frentes de trabalho"
                    aria-orientation="vertical"
                    onKeyDown={teclasDasAbas}
                  >
                    {frentes.map((frente, index) => {
                      const ativo = index === Math.min(negocioAtivo, frentes.length - 1);
                      return (
                        <S.BizTab
                          key={`${frente?.nome}-${index}`}
                          type="button"
                          role="tab"
                          id={`negocio-aba-${index}`}
                          aria-selected={ativo}
                          aria-controls="negocio-painel"
                          // Só a aba ativa entra na navegação por Tab: a saída
                          // da lista é o Tab, e as setas andam dentro dela.
                          tabIndex={ativo ? 0 : -1}
                          $ativo={ativo}
                          onClick={() => setNegocioAtivo(index)}
                        >
                          {cortar(frente?.nome, LIMITE_NEGOCIO.nome)}
                        </S.BizTab>
                      );
                    })}
                  </S.BizTabs>

                  <S.BizBody
                    id="negocio-painel"
                    role="tabpanel"
                    aria-labelledby={`negocio-aba-${Math.min(negocioAtivo, frentes.length - 1)}`}
                    $comFoto={Boolean(frenteAberta?.imagem?.[0])}
                  >
                    <S.BizContent>
                      <S.BizEyebrow tabIndex="0">
                        {cortar(frenteAberta?.nome, LIMITE_NEGOCIO.nome)}
                      </S.BizEyebrow>
                      <h3 tabIndex="0">
                        {cortar(frenteAberta?.titulo, LIMITE_NEGOCIO.titulo)}
                      </h3>
                      {/* O title leva o texto inteiro: o corte é para o layout,
                          não para esconder informação de quem quiser ler. */}
                      <p tabIndex="0" title={frenteAberta?.descricao || undefined}>
                        {cortar(frenteAberta?.descricao, LIMITE_NEGOCIO.descricao)}
                      </p>
                      {(frenteAberta?.acao?.destino ||
                        frenteAberta?.acao?.arquivo?.[0]) && (
                        <S.BizLink
                          type="button"
                          onClick={() => abrirAcao(frenteAberta?.acao)}
                        >
                          {frenteAberta?.acao?.rotulo}
                          <ArrowRight size={16} className="arrow-icon" />
                        </S.BizLink>
                      )}
                    </S.BizContent>
                    {frenteAberta?.imagem?.[0] && (
                      // A key troca o <img> a cada aba: trocar só o src deixava
                      // a foto da aba anterior na tela até a nova chegar.
                      <S.BizMedia
                        key={frenteAberta.imagem[0]}
                        src={fotoDaFrente(frenteAberta.imagem[0])}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = getRawAssetUrl(frenteAberta.imagem[0]);
                        }}
                        alt={`foto de ${frenteAberta?.nome}`}
                        decoding="async"
                      />
                    )}
                  </S.BizBody>
                </S.BizPanel>
              </S.Reveal>
            </S.Interior>
          </S.Faixa>
        )}

        <S.Faixa $sangra $imagem={videoBannerBgUrl}>
          <S.VideoBannerBox>

            <S.VideoBoxPlay className="video-wrapper">
              <S.VideoPhoneBoxMold
                src={videoPhoneMoldUrl}
                className='mold'
              >
                {
                  videoPlay ? (
                    <S.VideoBox>
                      <ReactPlayer
                        className="react-player"
                        url={faixaVideo?.enderecoDoVideo}
                        width="100%"
                        height="100%"
                        muted={!openModal}
                        controls={true}
                        playing={true}
                      />
                    </S.VideoBox>
                  ) : (<button onClick={() => { setClickVideoOpen(!clickVideoOpen); setVideoPlay(true) }}> <PlayCircle className='icon' /></button>)
                }
              </S.VideoPhoneBoxMold>

              <S.ButtonController style={{ display: videoPlay ? 'flex' : 'none' }}>
                <button onClick={() => setVideoPlay(false)}><CircleStop class="icon" /> Parar</button>
                <button onClick={() => { setOpenModal(true); setVideoPlay(false) }}><Expand class="icon" /> Expandir</button>
                <button onClick={() => navigate('/galeriaDeVideos')}><Images class="icon" /> Galeria de videos</button>
              </S.ButtonController>

            </S.VideoBoxPlay>

            <S.VideoBannerContent>
              <span>{faixaVideo?.chapeu}</span>
              <h2>{faixaVideo?.titulo}</h2>
              <p>{faixaVideo?.descricao}</p>
            </S.VideoBannerContent>
          </S.VideoBannerBox>
        </S.Faixa>

        <S.Faixa $topoCurto aria-labelledby="app-t">
          {/* O traço amarelo é desenho, não conteúdo: fica no código, sangra
              de ponta a ponta e não captura clique. */}
          <S.AppTraco viewBox="0 0 1440 170" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0,112 C520,158 1040,140 1440,38" />
          </S.AppTraco>

          <S.AppBox>
            <S.AppTexto>
              <h2 id="app-t" tabIndex="0">{faixaPodcast?.titulo}</h2>
              <ReactMarkdown children={faixaPodcast?.descricao} tabIndex="0" />
              {(faixaPodcast?.acao?.destino || faixaPodcast?.acao?.arquivo?.[0]) && (
                <S.AppBotao
                  type="button"
                  onClick={() => abrirAcao(faixaPodcast?.acao)}
                >
                  <span>{faixaPodcast?.acao?.rotulo}</span>
                  <span className="selo" aria-hidden="true">
                    <Play size={13} fill="currentColor" />
                  </span>
                </S.AppBotao>
              )}
            </S.AppTexto>

            {faixaPodcast?.imagemDestaque?.[0] && (
              <S.AppImagem
                src={getAssetUrl(faixaPodcast.imagemDestaque[0], {
                  width: 840,
                  quality: 85,
                  format: 'webp',
                })}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = getRawAssetUrl(faixaPodcast.imagemDestaque[0]);
                }}
                alt="mão segurando o celular com o app Copasul"
                loading="lazy"
                decoding="async"
              />
            )}
          </S.AppBox>
        </S.Faixa>

      </S.Container>
    );
  }
};
